import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {css, StyleSheet} from "aphrodite";
import {compose} from "mu-lambda";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {addStyle, View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import Link from "@khanacademy/wonder-blocks-link";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    Body,
    Caption,
    HeadingMedium,
    HeadingSmall,
    HeadingXSmall,
    LabelLarge,
    Tagline,
    Title,
} from "@khanacademy/wonder-blocks-typography";
import {mediaQueries} from "@khan/shared-styles";
import {staticUrl} from "@khan/urls";

import PageRoot from "#app-shell-package/page-root.tsx";
import withApollo, {
    type WithApolloProps,
    type WithoutApollo,
} from "#data-access/gql/with-apollo.tsx";
import withLocation, {
    type WithLocationProps,
    type WithoutLocation,
} from "#data-access/with-location.tsx";

import {
    fetchCareersData,
    formatCareersData,
    type JobListing,
    type JobListingSection,
} from "./careers-data-provider.ts";
import {withStrings} from "./locales/careers-page/load-strings.tsx";

const OpeningsSectionHeader = (props: {title: string}) => (
    <View style={styles.openingSectionHeader}>
        <HeadingMedium style={styles.serif}>{props.title}</HeadingMedium>
    </View>
);

const Opening = (props: {listing: JobListing}) => {
    return (
        <View style={styles.opening}>
            <LabelLarge>
                <Link href={props.listing.url} target="_blank">
                    {props.listing.title}
                </Link>
            </LabelLarge>
            <HeadingXSmall style={{color: color.offBlack64}}>
                {props.listing.location}
            </HeadingXSmall>
        </View>
    );
};

const OpeningsSection = (props: {section: JobListingSection}) => (
    <React.Fragment>
        <OpeningsSectionHeader title={props.section.name} />
        {props.section.jobs.map((x) => (
            <Opening key={x.url} listing={x} />
        ))}
    </React.Fragment>
);

const OpeningsEmptyState = () => (
    <View style={styles.opening}>
        <Body style={{textAlign: "center"}}>
            {t`There are no openings at this time. Please check back later!`}
        </Body>
    </View>
);

const CoreValue = (props: {title: string; body: string}) => (
    <View>
        <HeadingSmall style={[styles.prose, {fontWeight: "bold"}]}>
            {props.title}
        </HeadingSmall>
        <Body style={styles.prose}>{props.body}</Body>
    </View>
);

const Perk = (props: {title: string; imageSrc: string; isLast?: boolean}) => (
    <View style={[styles.perk, props.isLast && styles.perkLast]}>
        <StyledImg
            style={styles.perkImage}
            src={staticUrl(`/images/careers/perks/${props.imageSrc}`)}
            alt=""
        />
        <Strut size={spacing.xxxSmall_4} />
        <HeadingSmall>{props.title}</HeadingSmall>
    </View>
);

const StyledImg = addStyle("img");

type Props = WithLocationProps & WithApolloProps;

type State = {
    careers: Array<JobListingSection> | null | undefined;
    careersLoaded: boolean;
};
class CareersPage extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {careers: [], careersLoaded: false};
    }

    componentDidMount() {
        this._fetchCareers();
    }

    _fetchCareers: () => Promise<void> = async () => {
        const careers = await fetchCareersData(this.props.gqlClient);
        // This is to support greenhouse tracking. Decided to pass them all
        // through so that if greenhouses's API changes again we don't have to do anything
        // support.greenhouse.io/hc/en-us/articles/216904366-External-Job-Board-Tracking-Links-Overview
        const queryParams = this.props.location.queryParams;

        this.setState({
            careersLoaded: true,
        });
        if (careers) {
            this.setState({
                careers: formatCareersData(careers, queryParams),
            });
        }
    };

    render(): React.ReactNode {
        const hero = (
            <View style={styles.heroContainer}>
                <StyledImg
                    style={[styles.scalingPhoto, {maxWidth: 530}]}
                    src={staticUrl("/images/careers/careers-hero.png")}
                    alt={t`A student studying on their laptop`}
                />
                <Strut size={spacing.medium_16} />
                <Title style={styles.heading}>
                    {/*
                NOTE (bryan): On large displays, we don't want to end a line of
                text with the word "but" - so on those displays, we'll force a
                line-wrap there using this little trick. There were some bugs in
                I18nMarkup that prevented self-closing tags - but I hope this
                explains what we're doing here!
            */}
                    <Trans>
                        We’ve got an ambitious mission,
                        <br className={css(styles.titleLineBreak)} /> but
                        together we can achieve it.
                    </Trans>
                </Title>
                <Strut size={spacing.medium_16} />
                <Tagline style={[styles.prose, {textAlign: "center"}]}>
                    {t`Every child deserves the chance to learn regardless of where they are or their circumstances.`}
                </Tagline>
                <Strut size={spacing.xLarge_32} />
                <View style={styles.heroButtonContainer}>
                    <Button href="/careers#openings" style={styles.heroButton}>
                        {t`View careers`}
                    </Button>
                    <Strut size={spacing.medium_16} />
                    <Button
                        href="/careers/interns"
                        kind="secondary"
                        style={styles.heroButton}
                    >
                        {t`View internships`}
                    </Button>
                </View>
            </View>
        );

        const subhero = (
            <View style={styles.subheroContainer}>
                <View style={styles.subheroImageContainer}>
                    <StyledImg
                        style={styles.scalingPhoto}
                        src={staticUrl("/images/careers/careers-subhero.png")}
                        alt="A teacher helping a student."
                    />
                </View>
                <View>
                    <Title style={styles.serif}>
                        {t`We’re driven by our community.`}
                    </Title>
                    <Strut size={spacing.large_24} />
                    <Tagline style={styles.prose}>
                        {t`Our students, teachers, and parents come from all walks of life and so do we. We hire great people from a wide variety of backgrounds, not just because it’s the right thing to do, but because it makes our company stronger. Valuing diversity, equity and inclusion not only matters, but is necessary for us to actualize our mission and truly impact the communities we serve.`}
                    </Tagline>
                </View>
            </View>
        );

        const coreValues = (
            <View style={styles.grayContainer}>
                <Strut size={spacing.xxxLarge_64} />
                <Title style={styles.heading}>{t`Our core values`}</Title>
                <View style={styles.valuesContainer}>
                    <CoreValue
                        title={t`Live and breathe learners`}
                        body={t`We deeply understand and empathize with our users. We leverage user insights, research, and experience to build content, products, services, and experiences that our users trust and love. Our success is defined by the success of our learners and educators.`}
                    />
                    <CoreValue
                        title={t`Take a stand`}
                        body={t`As a company, we have conviction in our aspirational point of view of how education will evolve. The work we do is in service to moving towards that point of view. However, we also listen, learn and flex in the face of new data, and commit to evolving this point of view as the industry and our users evolve.`}
                    />
                    <CoreValue
                        title={t`Embrace diverse perspectives`}
                        body={t`We are a diverse community. We seek out and embrace a diversity of voices, perspectives and life experiences leading to stronger, more inclusive teams and better outcomes. As individuals, we are committed to bringing up tough topics and leaning into different points of view with curiosity. We actively listen, learn and collaborate to gain a shared understanding. When a decision is made, we commit to moving forward as a united team.`}
                    />
                    <CoreValue
                        title={t`Work responsibly and sustainably`}
                        body={t`We understand that achieving our audacious mission is a marathon, so we set realistic timelines and we focus on delivery that also links to the bigger picture. As a non-profit, we are supported by the generosity of donors as well as strategic partners, and understand our responsibility to our finite resources. We spend every dollar as though it were our own. We are responsible for the impact we have on the world and to each other. We ensure our team and company stay healthy and financially sustainable.`}
                    />
                    <CoreValue
                        title={t`Deliver wow`}
                        body={t`We insist on high standards and deliver delightful, effective end-to-end experiences that our users can rely on. We choose to focus on fewer things - each of which aligns to our ambitious vision - so we can deliver high-quality experiences that accelerate positive measurable learning with our strategic partners.`}
                    />
                    <CoreValue
                        title={t`Cultivate learning mindset`}
                        body={t`We believe in the power of growth for learners and for ourselves. We constantly learn and teach to improve our offerings, ourselves, and our organization. We learn from our mistakes and aren’t afraid to fail. We don't let past failures or successes stop us from taking future bold action and achieving our goals.`}
                    />
                    <CoreValue
                        title={t`Bring out the joy`}
                        body={t`We are committed to making learning a joyful process. This informs what we build for our users and the culture we co-create with our teammates, partners and donors.`}
                    />
                </View>
            </View>
        );

        const team = (
            <View style={styles.teamContainer}>
                <Title style={styles.heading}>{t`Meet our team`}</Title>
                <Strut size={spacing.xLarge_32} />
                {/* eslint-disable-next-line jsx-a11y/img-redundant-alt -- TODO: Address a11y error */}
                <StyledImg
                    style={styles.scalingPhoto}
                    src={staticUrl("/images/careers/khansite-2020.jpg")}
                    alt={t`Team photo from January 2020.`}
                />
                <Strut size={spacing.large_24} />
                <Caption style={styles.serif}>
                    {t`Team photo from January 2020.`}
                </Caption>
                <Strut size={spacing.xLarge_32} />
                <Button href="/about/the-team">{t`Learn more about us`}</Button>
                <Strut size={spacing.xxxLarge_64} />
            </View>
        );
        const perks = (
            <View style={styles.grayContainer}>
                <Strut size={spacing.xxxLarge_64} />
                <Title style={styles.heading}>{t`Our perks`}</Title>
                <Strut size={spacing.xxxLarge_64} />
                <View style={styles.perksContainer}>
                    <Perk
                        title={t`Competitive salaries`}
                        imageSrc="competitive-salaries.png"
                    />
                    <Perk
                        title={t`Health and wellness benefits`}
                        imageSrc="health-and-wellness.png"
                    />
                    <Perk
                        title={t`Flexible time off`}
                        imageSrc="flexible-time-off.png"
                    />
                    <Perk
                        title={t`401(k) + matching`}
                        imageSrc="401k-matching.png"
                    />
                    <Perk
                        title={t`Affinity groups`}
                        imageSrc="affinity-groups.png"
                    />
                    <Perk
                        title={t`Paid parental leave`}
                        imageSrc="paid-parental-leave.png"
                    />
                    <Perk
                        title={t`Fun virtual events`}
                        imageSrc="virtual-events.png"
                        isLast={true}
                    />
                </View>
                <Strut size={spacing.xxxLarge_64} />
            </View>
        );

        const openings = (
            <View id="openings" style={styles.openingsContainer}>
                <Strut size={spacing.xxxLarge_64} />
                <Title style={styles.heading}>{t`Current openings`}</Title>
                <Strut size={spacing.xxxLarge_64} />
                {this.state.careers && this.state.careersLoaded ? (
                    <React.Fragment>
                        {this.state.careers.length === 0 && (
                            <OpeningsEmptyState />
                        )}
                        {this.state.careers.map((x, i) => (
                            <OpeningsSection key={i} section={x} />
                        ))}
                    </React.Fragment>
                ) : (
                    <View style={styles.openingsLoading}>
                        <CircularSpinner size="medium" />
                    </View>
                )}

                <Strut size={128} />
            </View>
        );

        return (
            <PageRoot
                settings={{
                    title: t`Careers`,
                }}
            >
                <View style={{alignItems: "center"}}>
                    <Strut size={spacing.xxLarge_48} />
                    {hero}
                    <Strut size={144} />
                    {subhero}
                    <Strut size={144} />
                    {coreValues}
                    <Strut size={spacing.xxxLarge_64} />
                    {team}
                    {perks}
                    {openings}
                </View>
            </PageRoot>
        );
    }
}

const MAX_WIDTH = 1024;
const GRID_COLUMN_GAP = spacing.xxxLarge_64;

const styles = StyleSheet.create({
    serif: {
        fontFamily: "'Source Serif Pro', 'Noto Serif', serif",
    },
    heading: {
        fontFamily: "'Source Serif Pro', 'Noto Serif', serif",
        textAlign: "center",
    },
    titleLineBreak: {
        [mediaQueries.smOrSmaller]: {
            display: "none",
        },
    },
    heroContainer: {
        alignItems: "center",
        maxWidth: 670,
        paddingLeft: spacing.medium_16,
        paddingRight: spacing.medium_16,

        [mediaQueries.mdOrSmaller]: {
            paddingLeft: spacing.xLarge_32,
            paddingRight: spacing.xLarge_32,
        },

        [mediaQueries.smOrSmaller]: {
            width: "100%",
            paddingLeft: spacing.medium_16,
            paddingRight: spacing.medium_16,
        },
    },
    heroButtonContainer: {
        flexDirection: "row",
        justifyContent: "center",

        [mediaQueries.smOrSmaller]: {
            flexDirection: "column",
        },
    },
    heroButton: {
        minWidth: 172,
    },
    grayContainer: {
        width: "100%",
        position: "relative",
        backgroundColor: "#F5F5F5",
    },
    subheroContainer: {
        display: "grid",
        gridTemplateColumns: "auto auto",
        width: MAX_WIDTH,
        margin: "auto",
        gridColumnGap: GRID_COLUMN_GAP,
        paddingLeft: spacing.medium_16,
        paddingRight: spacing.medium_16,

        [mediaQueries.mdOrSmaller]: {
            width: "100%",
            paddingLeft: spacing.xLarge_32,
            paddingRight: spacing.xLarge_32,
        },

        [mediaQueries.smOrSmaller]: {
            width: "100%",
            gridTemplateColumns: "auto",
            paddingLeft: spacing.medium_16,
            paddingRight: spacing.medium_16,
        },
    },
    subheroImageContainer: {
        [mediaQueries.smOrSmaller]: {
            marginTop: spacing.medium_16,
            order: 1,
        },
    },
    valuesContainer: {
        display: "grid",
        gridTemplateColumns: "auto auto",
        width: MAX_WIDTH,
        margin: "auto",
        paddingLeft: spacing.medium_16,
        paddingRight: spacing.medium_16,

        gridRowGap: spacing.xxLarge_48,
        gridColumnGap: GRID_COLUMN_GAP,
        paddingTop: spacing.xxxLarge_64,
        paddingBottom: spacing.xxxLarge_64,

        [mediaQueries.md]: {
            width: "100%",
            paddingLeft: spacing.xLarge_32,
            paddingRight: spacing.xLarge_32,
        },

        [mediaQueries.smOrSmaller]: {
            width: "100%",
            gridTemplateColumns: "auto",
            paddingLeft: spacing.medium_16,
            paddingRight: spacing.medium_16,
        },
    },
    perksContainer: {
        display: "grid",
        gridTemplateColumns: "auto auto auto",
        gridRowGap: spacing.xxxLarge_64,
        width: MAX_WIDTH,
        margin: "auto",
        paddingLeft: spacing.medium_16,
        paddingRight: spacing.medium_16,

        [mediaQueries.md]: {
            width: "100%",
            paddingLeft: spacing.xLarge_32,
            paddingRight: spacing.xLarge_32,
            gridTemplateColumns: "auto auto",
        },

        [mediaQueries.smOrSmaller]: {
            width: "100%",
            paddingLeft: spacing.medium_16,
            paddingRight: spacing.medium_16,
            gridTemplateColumns: "auto",
        },
    },
    perk: {
        alignItems: "center",
    },
    perkLast: {
        // NOTE (bryan): When our seven perks are in a three-column layout, we
        // want that last perk to be centered, not left-aligned. Using
        // gridColumn: 2 causes it to go in the second column (so it's
        // centered). Then, when we're in a 2-column or 1-column layout
        // (mdOrSmaller), we can let it just sit in the first column!
        gridColumn: 2,

        [mediaQueries.mdOrSmaller]: {
            gridColumn: 1,
        },
    },
    perkImage: {
        width: 90,
        height: 90,
    },
    prose: {
        lineHeight: 30 / 18,

        [mediaQueries.smOrSmaller]: {
            lineHeight: 16 / 12,
        },
    },
    teamContainer: {
        alignItems: "center",
        width: MAX_WIDTH,
        margin: "auto",

        [mediaQueries.md]: {
            width: "100%",
            paddingLeft: spacing.xLarge_32,
            paddingRight: spacing.xLarge_32,
        },

        [mediaQueries.smOrSmaller]: {
            width: "100%",
            paddingLeft: spacing.medium_16,
            paddingRight: spacing.medium_16,
        },
    },
    scalingPhoto: {
        width: "100%",
        height: "auto",
        objectFit: "scale-down",
    },
    openingsLoading: {
        alignItems: "center",
    },
    openingsContainer: {
        width: MAX_WIDTH,
        margin: "auto",
        paddingLeft: spacing.medium_16,
        paddingRight: spacing.medium_16,

        [mediaQueries.md]: {
            width: "100%",
            paddingLeft: spacing.xLarge_32,
            paddingRight: spacing.xLarge_32,
        },

        [mediaQueries.smOrSmaller]: {
            width: "100%",
            paddingLeft: spacing.medium_16,
            paddingRight: spacing.medium_16,
        },
    },
    openingSectionHeader: {
        paddingTop: spacing.xLarge_32,
        paddingBottom: spacing.large_24,
        borderBottom: `1px solid ${color.offBlack32}`,
    },
    opening: {
        paddingTop: spacing.medium_16,
        paddingBottom: spacing.medium_16,
    },
});

type ExportProps = WithoutApollo<
    WithoutLocation<
        JSX.LibraryManagedAttributes<
            typeof CareersPage,
            React.ComponentProps<typeof CareersPage>
        >
    >
>;

export default compose(
    withStrings,
    withLocation,
    withApollo,
)(CareersPage) as React.ComponentType<ExportProps>;
